import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Trophy,
  Plus,
  Calendar,
  Users,
  ChevronRight,
  MoreVertical,
  Play,
  Pause,
  CheckCircle,
  XCircle,
  Edit,
  Trash2,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import type { Tournament, TournamentStatus } from "@shared/schema";

interface TournamentListProps {
  onCreateTournament: () => void;
  onViewTournament: (id: string) => void;
  onEditTournament: (id: string) => void;
}

const statusLabels: Record<TournamentStatus, string> = {
  draft: "Borrador",
  registration_open: "Inscripciones Abiertas",
  registration_closed: "Inscripciones Cerradas",
  in_progress: "En Progreso",
  completed: "Finalizado",
  cancelled: "Cancelado",
};

const statusColors: Record<TournamentStatus, string> = {
  draft: "bg-muted text-muted-foreground",
  registration_open: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  registration_closed: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
  in_progress: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
  completed: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400",
  cancelled: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
};

const formatLabels: Record<string, string> = {
  single_elimination: "Eliminatoria Simple",
  double_elimination: "Eliminatoria Doble",
  round_robin: "Todos contra Todos",
  groups_then_elimination: "Grupos + Eliminatoria",
};

const categoryLabels: Record<string, string> = {
  open: "Abierta",
  beginner: "Principiante",
  intermediate: "Intermedio",
  advanced: "Avanzado",
  pro: "Profesional",
  mixed: "Mixto",
  women: "Femenino",
  men: "Masculino",
};

export function TournamentList({
  onCreateTournament,
  onViewTournament,
  onEditTournament,
}: TournamentListProps) {
  const { toast } = useToast();
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const { data: tournaments, isLoading } = useQuery<Tournament[]>({
    queryKey: ["/api/club/tournaments"],
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/club/tournaments/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/club/tournaments"] });
      toast({
        title: "Torneo eliminado",
        description: "El torneo ha sido eliminado correctamente.",
      });
      setDeleteId(null);
    },
    onError: () => {
      toast({
        title: "Error",
        description: "No se pudo eliminar el torneo.",
        variant: "destructive",
      });
    },
  });

  const statusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: TournamentStatus }) => {
      await apiRequest("PATCH", `/api/club/tournaments/${id}/status`, { status });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/club/tournaments"] });
      toast({
        title: "Estado actualizado",
        description: "El estado del torneo ha sido actualizado.",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "No se pudo actualizar el estado.",
        variant: "destructive",
      });
    },
  });

  const formatDate = (date: Date | string | null) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("es-ES", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-40" />
        </div>
        <div className="grid gap-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-32 w-full" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Trophy className="h-6 w-6 text-primary" />
            Torneos
          </h2>
          <p className="text-muted-foreground text-sm mt-1">
            Gestiona los torneos de tu club
          </p>
        </div>
        <Button onClick={onCreateTournament} data-testid="button-create-tournament">
          <Plus className="h-4 w-4 mr-2" />
          Crear Torneo
        </Button>
      </div>

      {(!tournaments || tournaments.length === 0) ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Trophy className="h-16 w-16 mx-auto mb-4 text-muted-foreground/30" />
            <h3 className="text-lg font-semibold mb-2">No hay torneos</h3>
            <p className="text-muted-foreground mb-6">
              Crea tu primer torneo para empezar a gestionar competiciones
            </p>
            <Button onClick={onCreateTournament} data-testid="button-create-first-tournament">
              <Plus className="h-4 w-4 mr-2" />
              Crear Primer Torneo
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {tournaments.map((tournament) => (
            <Card 
              key={tournament.id} 
              className="hover-elevate overflow-visible cursor-pointer"
              onClick={() => onViewTournament(tournament.id)}
              data-testid={`card-tournament-${tournament.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <h3 className="font-semibold text-lg truncate">{tournament.name}</h3>
                      <Badge className={statusColors[tournament.status as TournamentStatus]}>
                        {statusLabels[tournament.status as TournamentStatus]}
                      </Badge>
                    </div>
                    
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        {formatDate(tournament.startDate)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="h-4 w-4" />
                        Máx. {tournament.maxTeams} equipos
                      </span>
                      <span>
                        {formatLabels[tournament.format || "single_elimination"]}
                      </span>
                      <span>
                        {categoryLabels[tournament.category || "open"]}
                      </span>
                    </div>

                    {tournament.description && (
                      <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                        {tournament.description}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" data-testid={`button-tournament-menu-${tournament.id}`}>
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenuItem onClick={() => onViewTournament(tournament.id)}>
                          <ChevronRight className="h-4 w-4 mr-2" />
                          Ver Detalles
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onEditTournament(tournament.id)}>
                          <Edit className="h-4 w-4 mr-2" />
                          Editar
                        </DropdownMenuItem>
                        
                        {tournament.status === "draft" && (
                          <DropdownMenuItem 
                            onClick={() => statusMutation.mutate({ id: tournament.id, status: "registration_open" })}
                          >
                            <Play className="h-4 w-4 mr-2" />
                            Abrir Inscripciones
                          </DropdownMenuItem>
                        )}
                        
                        {tournament.status === "registration_open" && (
                          <DropdownMenuItem 
                            onClick={() => statusMutation.mutate({ id: tournament.id, status: "registration_closed" })}
                          >
                            <Pause className="h-4 w-4 mr-2" />
                            Cerrar Inscripciones
                          </DropdownMenuItem>
                        )}
                        
                        {tournament.status === "in_progress" && (
                          <DropdownMenuItem 
                            onClick={() => statusMutation.mutate({ id: tournament.id, status: "completed" })}
                          >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Finalizar Torneo
                          </DropdownMenuItem>
                        )}
                        
                        {tournament.status !== "completed" && tournament.status !== "cancelled" && (
                          <DropdownMenuItem 
                            onClick={() => statusMutation.mutate({ id: tournament.id, status: "cancelled" })}
                            className="text-destructive"
                          >
                            <XCircle className="h-4 w-4 mr-2" />
                            Cancelar Torneo
                          </DropdownMenuItem>
                        )}
                        
                        <DropdownMenuItem 
                          onClick={() => setDeleteId(tournament.id)}
                          className="text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Eliminar
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar torneo?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminarán todos los equipos y partidos asociados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteId && deleteMutation.mutate(deleteId)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
